import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Users,
  Package,
  Briefcase,
  FileText,
  DollarSign,
  Heart,
  ShoppingCart,
  TrendingUp,
} from 'lucide-react';

const DashboardPage = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getDashboardStats();
      
      if (isAdmin()) {
        setStats([
          { title: 'Usuários', value: data.users?.toString() || '0', icon: Users, color: 'text-blue-600' },
          { title: 'Produtos', value: data.products?.toString() || '0', icon: Package, color: 'text-green-600' },
          { title: 'Serviços', value: data.services?.toString() || '0', icon: Briefcase, color: 'text-purple-600' },
          { title: 'Faturas', value: data.invoices?.toString() || '0', icon: FileText, color: 'text-orange-600' },
          { title: 'Despesas', value: formatCurrency(data.expenses || 0), icon: DollarSign, color: 'text-red-600' },
          { title: 'Doações', value: formatCurrency(data.donations || 0), icon: Heart, color: 'text-pink-600' },
          { title: 'Vendas PDV', value: formatCurrency(data.pdvSales || 0), icon: ShoppingCart, color: 'text-cyan-600' },
          { title: 'Receita', value: formatCurrency(data.revenue || 0), icon: TrendingUp, color: 'text-emerald-600' },
        ]);
      } else {
        setStats([
          { title: 'Meus Serviços', value: data.myServices?.toString() || '0', icon: Briefcase, color: 'text-purple-600' },
          { title: 'Serviços Ativos', value: data.activeServices?.toString() || '0', icon: TrendingUp, color: 'text-green-600' },
          { title: 'Minhas Faturas', value: data.myInvoices?.toString() || '0', icon: FileText, color: 'text-orange-600' },
          { title: 'Total Receber', value: formatCurrency(data.totalToReceive || 0), icon: DollarSign, color: 'text-blue-600' },
        ]);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      // Fallback para valores padrão em caso de erro
      if (isAdmin()) {
        setStats([
          { title: 'Usuários', value: '0', icon: Users, color: 'text-blue-600' },
          { title: 'Produtos', value: '0', icon: Package, color: 'text-green-600' },
          { title: 'Serviços', value: '0', icon: Briefcase, color: 'text-purple-600' },
          { title: 'Faturas', value: '0', icon: FileText, color: 'text-orange-600' },
          { title: 'Despesas', value: 'R$ 0', icon: DollarSign, color: 'text-red-600' },
          { title: 'Doações', value: 'R$ 0', icon: Heart, color: 'text-pink-600' },
          { title: 'Vendas PDV', value: 'R$ 0', icon: ShoppingCart, color: 'text-cyan-600' },
          { title: 'Receita', value: 'R$ 0', icon: TrendingUp, color: 'text-emerald-600' },
        ]);
      } else {
        setStats([
          { title: 'Meus Serviços', value: '0', icon: Briefcase, color: 'text-purple-600' },
          { title: 'Serviços Ativos', value: '0', icon: TrendingUp, color: 'text-green-600' },
          { title: 'Minhas Faturas', value: '0', icon: FileText, color: 'text-orange-600' },
          { title: 'Total Receber', value: 'R$ 0', icon: DollarSign, color: 'text-blue-600' },
        ]);
      }
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Bem-vindo de volta, <span className="font-medium">{user?.name}</span>
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {loading ? (
          <div className="col-span-full text-center text-muted-foreground py-8">
            Carregando estatísticas...
          </div>
        ) : (
          stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="hover:shadow-lg transition-shadow duration-200"
                data-testid={`stat-card-${index}`}
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                  <Icon className={`w-5 h-5 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Welcome Card */}
      <Card className="border-2 border-dashed" data-testid="welcome-card">
        <CardHeader>
          <CardTitle>Bem-vindo ao KawaTenda! 🎉</CardTitle>
          <CardDescription className="text-base">
            {isAdmin()
              ? 'Como administrador, você tem acesso completo a todas as funcionalidades do sistema.'
              : 'Você pode visualizar seus serviços, faturas e executar serviços ativos.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">Recursos disponíveis:</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {isAdmin() ? (
                <>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Gerenciar usuários e permissões
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Controlar produtos, serviços e estoque
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Processar vendas no PDV
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Gerenciar faturamento e despesas
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Organizar agenda e eventos
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Visualizar e executar seus serviços
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Acompanhar suas faturas
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Ver produtos disponíveis
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Consultar sua agenda
                  </li>
                </>
              )}
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
